/**
 * The Auditor tab: one entry per filing -- firm, signer, licence, opinion
 * type, report date (both Gregorian and the raw Hijri string as printed),
 * and the key audit matters for annual filings (§10.3).
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { AuditorRow } from '@server/sql/notes.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { date } from '~/i18n/format.ts'
import { Badge, Field, Label, NotRecorded, Panel, Skeleton } from '~/components/ui/primitives.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'

export function CompanyAuditor({ symbol }: { symbol: string }) {
  const { t, locale, v } = useT()
  const { data, error, refetch } = useQuery({
    queryKey: keys.companyAuditors(symbol),
    queryFn: () => api<{ auditors: AuditorRow[] }>(`/api/companies/${encodeURIComponent(symbol)}/auditors`),
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-72 w-full" />
  if (data.auditors.length === 0) return <EmptyState title={t('empty.generic')} />

  return (
    <div className="space-y-2">
      {data.auditors.map((a) => (
        <Panel key={a.id} className="px-3 py-2.5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Link
              href={`/c/${symbol}/d/${a.documentId}${a.pageNo ? `?p=${a.pageNo}` : ''}`}
              className="tnum text-[length:var(--text-data)] font-medium link-inline"
            >
              {a.period && (v('period', a.period) ?? a.period)} {a.fiscalYear}
            </Link>
            <Raw lang={a.locale} className="text-[length:var(--text-data)] text-[var(--text)]">
              {a.firmNameRaw ?? ''}
            </Raw>
            {a.isJoint && <Badge state="accent">joint</Badge>}
            <div className="flex-1" />
            {a.opinionType && (
              <Badge state={/unqualified|clean/i.test(a.opinionType) ? 'pass' : 'neutral'}>
                <Untranslated>{a.opinionType}</Untranslated>
              </Badge>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
            <Field label={t('company.signer')}>
              {a.signerNameRaw
                ? <Raw lang={a.locale}>{a.signerNameRaw}</Raw>
                : <NotRecorded>{t('common.notRecorded')}</NotRecorded>}
            </Field>
            <Field label={t('company.licence')} mono>
              {a.signerLicense ?? <NotRecorded>{t('common.notRecorded')}</NotRecorded>}
            </Field>
            {a.firmRegistration && (
              <Field label={t('company.firm')} mono>{a.firmRegistration}</Field>
            )}
            <Field label={t('company.reportDate')}>
              {a.reportDateG
                ? date(a.reportDateG, locale)
                : <NotRecorded>{t('common.notRecorded')}</NotRecorded>}
            </Field>
            {/* The Hijri string is shown exactly as printed, unparsed (§11.3). */}
            {a.reportDateHRaw && (
              <Field label="Hijri"><Raw lang="ar">{a.reportDateHRaw}</Raw></Field>
            )}
          </div>

          {a.kam.length > 0 && (
            <div className="mt-2 border-t border-[var(--border)] pt-2">
              <Disclosure
                summary={t('viewer.kam')}
                count={a.kam.length}
                defaultOpen={data.auditors.length <= 2}
              >
                <div className="space-y-2 ps-4 pt-1">
                  {a.kam.map((k) => (
                    <div key={k.id} className="border-s border-[var(--border)] ps-3">
                      {k.titleRaw && (
                        <Raw lang={k.lang} className="block text-[length:var(--text-data)] font-medium">
                          {k.titleRaw}
                        </Raw>
                      )}
                      {k.matterRaw && (
                        <Raw as="p" lang={k.lang} className="mt-1 max-w-[92ch] text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)]">
                          {k.matterRaw}
                        </Raw>
                      )}
                      {k.responseRaw && (
                        <>
                          <Label className="mt-2">{t('quality.detail')}</Label>
                          <Raw as="p" lang={k.lang} className="mt-0.5 max-w-[92ch] text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)] text-[var(--text-muted)]">
                            {k.responseRaw}
                          </Raw>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </Disclosure>
            </div>
          )}
        </Panel>
      ))}
    </div>
  )
}
